import { createContext, useContext, useMemo } from 'react';
import useBannerHelpers, { BannerHelpers } from '../utils/use-banner-helpers';

type MobileLayout = 'HORIZONTAL' | 'VERTICAL' | 'SHORT';
type DesktopLayout = 'PACKED' | 'SPACED';
type TextPosition = 'LEFT' | 'CENTER' | 'RIGHT';
type TextAlignment = 'LEFT' | 'CENTER';
type ImagePosition = 'LEFT' | 'RIGHT';

interface EnhancedBannerImageSource {
	type: string;
	srcSet: string;
}

export interface EnhancedBannerImage {
	path: string;
	file: string;
	src: string;
	srcSet: string;
	sources: EnhancedBannerImageSource[];
}

export interface Banner {
	id: number;
	name: string;
	headerText?: string;
	descriptionText?: string;
	textPosition: TextPosition;
	textAlign: TextAlignment;
	backgroundImage: EnhancedBannerImage;
	url?: string;
	ctaText?: string;
	tcText?: string;
	tcImage?: EnhancedBannerImage;
	foregroundImage1?: EnhancedBannerImage;
	foregroundImage1Position?: ImagePosition;
	foregroundImage2?: EnhancedBannerImage;
	mobileLayoutType: MobileLayout;
	desktopLayoutType: DesktopLayout;
	openNewWindow: boolean;
}

export interface BannerContextProviderProps {
	children: React.ReactNode;
	data: Banner;
	index: number;
}

interface BannerContextValue {
	data: Banner;
	helpers: BannerHelpers;
}

const BannerContext = createContext({} as BannerContextValue);

export const useBannerContext = () => useContext(BannerContext);

export const BannerContextProvider: React.FC<BannerContextProviderProps> = ({ children, data, index }) => {
	const helpers = useBannerHelpers(data, index);

	const value = useMemo(
		() => ({
			data,
			helpers
		}),
		[data, helpers]
	);

	return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
};
